// ============================================================================
// NETWORK RESILIENCE - Retry, backoff, offline detection, and queuing
// ----------------------------------------------------------------------------
// Wraps fetch calls with:
//   1. Exponential backoff retry (configurable attempts + base delay)
//   2. Online/offline detection with event-driven recovery
//   3. Request queue for offline-submitted operations
//   4. Timeout enforcement (prevents hanging S3 uploads)
//   5. One classification of "the network dropped this" (isTransientNetworkFailure)
//      and one normalised error shape for it (normaliseNetworkError), so a
//      TypeError('Failed to fetch') from Brave, 'Load failed' from Safari and a
//      502 from the gateway all reach the operator as the same thing.
//   6. The backend-reachability signal behind the "Backend unreachable" banner.
//
// Who actually imports this (2026-09-22, after the site-visit field hardening):
//   • data-service-api.js  - apiFetch normalises network failures through
//     normaliseNetworkError, honours an opt-in `retry` option via
//     withNetworkRetry, and calls markBackendReachable on every 2xx.
//   • file-upload-service.js - the trusted-image POST, presign, direct PUT,
//     backend direct-upload and confirm legs run under withNetworkRetry.
//   • data-service.js - the banner state lives here (isBackendReachable /
//     subscribeBackendReachability) and the Reconnect button probes
//     probeQuoteMakerHealth before it reports success.
//   • App.tsx / OfflineBanner.tsx - onConnectionChange / isOnline.
// Before 2026-09-22 the header claimed the first two and neither imported it;
// every fetch in those files was bare.
// ============================================================================

// ─── Online/Offline State ────────────────────────────────────────────────────

const HEALTH_PROBE_TIMEOUT_MS = 3500;
let _isOnline = true;
let _connectivityProbeGeneration = 0;
const _onlineListeners = new Set<(online: boolean) => void>();

/**
 * Verify that QuoteMaker's own same-origin API is reachable. Browsers expose
 * navigator.onLine as a best-effort hint only; embedded browsers can report
 * false even while localhost is healthy.
 */
export async function probeQuoteMakerHealth(
    fetchImpl: typeof fetch = globalThis.fetch?.bind(globalThis),
    timeoutMs = HEALTH_PROBE_TIMEOUT_MS,
) {
    if (typeof fetchImpl !== 'function') return false;

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
        const timeout = new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => {
                controller?.abort();
                reject(new Error('QuoteMaker health check timed out'));
            }, timeoutMs);
        });
        const response = await Promise.race([
            fetchImpl('/api/health', {
                cache: 'no-store',
                credentials: 'same-origin',
                signal: controller?.signal,
            }),
            timeout,
        ]);
        return response.ok;
    } catch {
        return false;
    } finally {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
    }
}

function setConnectionState(online: boolean) {
    if (_isOnline === online) return;

    _isOnline = online;
    if (online) {
        console.log('[Network] QuoteMaker service connection restored');
        drainQueue(); // Process queued operations
    } else {
        console.warn('[Network] QuoteMaker service is unreachable - operations will be queued');
    }
    _onlineListeners.forEach(fn => fn(online));
}

function isTrustedLocalQuoteMakerHost() {
    if (typeof window === 'undefined') return false;
    const { hostname } = window.location;
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

async function verifyAfterOfflineHint() {
    const probeGeneration = ++_connectivityProbeGeneration;

    // Embedded browsers can serve localhost while reporting the wider
    // internet offline, then reject fetch() before it reaches /api/health.
    // Keep the loaded local app usable; real request failures retain their
    // normal retry and queue handling.
    if (isTrustedLocalQuoteMakerHost()) {
        setConnectionState(true);
        return;
    }

    const reachable = await probeQuoteMakerHealth();
    if (probeGeneration === _connectivityProbeGeneration) setConnectionState(reachable);
}

// Guard the method, not just the global: this module is now imported by the
// API layer, and a host (or a test) that provides a partial `window` must not
// throw at import time and take every fetch down with it.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('online', () => {
        _connectivityProbeGeneration += 1;
        setConnectionState(true);
    });
    window.addEventListener('offline', () => { void verifyAfterOfflineHint(); });
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        void verifyAfterOfflineHint();
    }
}

/**
 * Subscribe to online/offline state changes.
 * @param {Function} listener - Called with (isOnline: boolean)
 * @returns {Function} Unsubscribe function
 */
export function onConnectionChange(listener: (online: boolean) => void) {
    _onlineListeners.add(listener);
    return () => _onlineListeners.delete(listener);
}

/**
 * Check whether QuoteMaker's service is reachable. Browser offline events are
 * verified against the service before this state changes.
 */
export function isOnline() {
    return _isOnline;
}

// ─── Exponential Backoff Retry ───────────────────────────────────────────────

/**
 * Execute an async function with exponential backoff retry.
 *
 * @param {Function} fn - Async function to execute
 * @param {Object} [opts]
 * @param {number} [opts.maxAttempts=3] - Max retry attempts (including first try)
 * @param {number} [opts.baseDelayMs=1000] - Base delay between retries (doubles each time)
 * @param {number} [opts.maxDelayMs=30000] - Cap on delay between retries
 * @param {number} [opts.timeoutMs=60000] - Timeout per attempt (default 60s)
 * @param {Function} [opts.shouldRetry] - Custom retry predicate: (error, attempt) → boolean
 * @param {AbortSignal} [opts.signal] - Cancellation signal
 * @returns {Promise<any>} Result of fn()
 */
interface RetryOptions {
    maxAttempts?: number;
    baseDelayMs?: number;
    maxDelayMs?: number;
    timeoutMs?: number;
    shouldRetry?: (err: any, attempt: number) => boolean;
    signal?: AbortSignal;
}

export async function withRetry(fn: () => Promise<any>, opts: RetryOptions = {}) {
    const {
        maxAttempts = 3,
        baseDelayMs = 1000,
        maxDelayMs = 30000,
        timeoutMs = 60000,
        shouldRetry = defaultShouldRetry,
        signal,
    } = opts;

    let lastError: any;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

        try {
            // Wrap with timeout
            const result = await withTimeout(fn(), timeoutMs);
            return result;
        } catch (err: any) {
            lastError = err;

            // Don't retry on cancellation
            if (err.name === 'AbortError') throw err;

            // Check if we should retry
            if (attempt >= maxAttempts || !shouldRetry(err, attempt)) {
                throw err;
            }

            // Exponential backoff with jitter
            const delay = Math.min(baseDelayMs * Math.pow(2, attempt - 1), maxDelayMs);
            const jitter = delay * 0.2 * Math.random(); // 0-20% jitter
            const totalDelay = Math.round(delay + jitter);

            console.warn(`[Network] Attempt ${attempt}/${maxAttempts} failed: ${err.message}. Retrying in ${totalDelay}ms...`);
            await sleep(totalDelay, signal);
        }
    }

    throw lastError;
}

/**
 * Default retry predicate. Retries on network errors and 5xx responses.
 * Does NOT retry on 4xx (client errors) - those won't succeed on retry.
 */
function defaultShouldRetry(err: any) {
    // Network errors (fetch failed, DNS, connection refused)
    if (err.name === 'TypeError' && err.message.includes('fetch')) return true;

    // Timeout errors
    if (err.name === 'TimeoutError' || err.message?.includes('timeout')) return true;

    // Server errors (5xx) - server may recover
    if (err.status >= 500) return true;

    // Rate limited (429) - try again after backoff
    if (err.status === 429) return true;

    // Don't retry client errors (400, 401, 403, 404, 409)
    return false;
}

// ─── Transient network failure: one classification, one error shape ─────────
// Field report 2026-09-22 (Android, Brave, 4G, real site inspection): every
// surface showed the operator a raw `Failed to fetch`, and one of them
// ("The visit did not send") was wrong - the server had committed and only
// the reply was lost. These helpers give the client one answer to "did the
// network drop this?" and one honest error to throw when it did.

export const NETWORK_UNREACHABLE_CODE = 'NETWORK_UNREACHABLE';
export const NETWORK_UNREACHABLE_MESSAGE = 'QuoteMaker could not be reached. Check your signal and try again.';

/**
 * True when `err` is what fetch() itself rejects with when the request never
 * produced an HTTP response: Chromium/Brave say `Failed to fetch`, Firefox
 * `NetworkError when attempting to fetch resource.`, Safari `Load failed`.
 * A fetch TypeError is a transport failure by spec; the message check keeps
 * an unrelated programming TypeError (undefined property read) out of the
 * retry loop.
 */
export function isNetworkFetchFailure(err: any): boolean {
    if (!err) return false;
    if (err.code === NETWORK_UNREACHABLE_CODE) return true;
    if (err.name !== 'TypeError') return false;
    const message = String(err.message || '');
    return /fetch|network|load failed/i.test(message);
}

/**
 * The retry predicate for a request that is safe to send again: the network
 * dropped it, the per-attempt timeout fired, or a gateway in front of the
 * backend answered 502/503/504 (a container restarting behind nginx / the
 * cloud load balancer). Deliberately NOT: 4xx (the request is wrong or the
 * caller is not allowed), 500 (a route bug repeats), 429 (the caller has its
 * own Retry-After handling), AbortError (the caller cancelled).
 */
export function isTransientNetworkFailure(err: any): boolean {
    if (!err) return false;
    if (err.name === 'AbortError') return false;
    if (isNetworkFetchFailure(err)) return true;
    if (err.name === 'TimeoutError') return true;
    const status = err.status;
    return status === 502 || status === 503 || status === 504;
}

interface NormaliseNetworkErrorOptions {
    /** HTTP method of the request that failed; decides `outcomeUnknown`. */
    method?: string;
    /** Tag for the unhandled-rejection filters (`qmSource`). */
    source?: string;
    /** Override the operator-facing message. */
    message?: string;
}

/**
 * Turn a transport failure into the error every consumer can read:
 *   message        operator-facing, names the signal not the stack
 *   status 0       "no HTTP response" (project-save-retry-policy already
 *                  treats 0 as automatic-retry)
 *   code           NETWORK_UNREACHABLE
 *   outcomeUnknown true for anything but GET/HEAD: the server MAY have
 *                  applied the request and lost the reply. A caller that
 *                  shows "did not send" on this flag is lying to the operator.
 *   cause          the original TypeError / TimeoutError for logs
 */
export function normaliseNetworkError(err: any, {
    method = 'GET',
    source = 'network',
    message = NETWORK_UNREACHABLE_MESSAGE,
}: NormaliseNetworkErrorOptions = {}) {
    const verb = String(method || 'GET').toUpperCase();
    const normalised = new Error(message) as Error & Record<string, unknown>;
    normalised.status = 0;
    normalised.code = NETWORK_UNREACHABLE_CODE;
    normalised.qmSource = source;
    normalised.networkFailure = true;
    normalised.retryable = true;
    normalised.definitive = false;
    normalised.outcomeUnknown = verb !== 'GET' && verb !== 'HEAD';
    normalised.cause = err;
    return normalised;
}

/**
 * Run one attempt of a request under a per-attempt timeout that ABORTS the
 * underlying fetch (withTimeout below only stops waiting; the socket stays
 * open). The attempt receives an AbortSignal to pass to fetch(). When the
 * timer fires the AbortError is re-thrown as a TimeoutError so withRetry
 * retries it; an abort from the caller's own `signal` stays an AbortError and
 * is never retried. `timeoutMs: 0` disables the timer (large-file PUTs).
 */
export async function runAttemptWithTimeout<T>(
    attempt: (signal?: AbortSignal) => Promise<T>,
    { timeoutMs = 0, signal }: { timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<T> {
    const wantsTimer = Number.isFinite(timeoutMs) && timeoutMs > 0;
    if ((!wantsTimer && !signal) || typeof AbortController !== 'function') {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        return attempt(signal);
    }
    const controller = new AbortController();
    let timedOut = false;
    const onCallerAbort = () => {
        try { controller.abort((signal as any)?.reason); } catch { controller.abort(); }
    };
    if (signal) {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        signal.addEventListener('abort', onCallerAbort, { once: true });
    }
    const timer = wantsTimer
        ? setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs)
        : null;
    try {
        return await attempt(controller.signal);
    } catch (err: any) {
        if (timedOut && err?.name === 'AbortError') {
            throw Object.assign(new Error(`Operation timed out after ${timeoutMs}ms`), { name: 'TimeoutError', cause: err });
        }
        throw err;
    } finally {
        if (timer) clearTimeout(timer);
        signal?.removeEventListener('abort', onCallerAbort);
    }
}

interface NetworkRetryOptions {
    /** Total attempts including the first (default 3). */
    attempts?: number;
    /** Per-attempt timeout that aborts the fetch; 0 disables (default 0). */
    timeoutMs?: number;
    baseDelayMs?: number;
    maxDelayMs?: number;
    /** The caller's cancellation. Stops retries and aborts the in-flight attempt. */
    signal?: AbortSignal;
    shouldRetry?: (err: any, attempt: number) => boolean;
}

/**
 * withRetry for the network-only case: the attempt gets an AbortSignal that
 * carries both the caller's cancellation and the per-attempt timeout, and only
 * isTransientNetworkFailure errors are retried. The caller asserts the request
 * is safe to repeat - this helper never checks the HTTP method.
 */
export function withNetworkRetry<T>(
    attempt: (signal?: AbortSignal) => Promise<T>,
    {
        attempts = 3,
        timeoutMs = 0,
        baseDelayMs = 1000,
        maxDelayMs = 30000,
        signal,
        shouldRetry = isTransientNetworkFailure,
    }: NetworkRetryOptions = {},
): Promise<T> {
    return withRetry(
        () => runAttemptWithTimeout(attempt, { timeoutMs, signal }),
        { maxAttempts: attempts, timeoutMs: 0, baseDelayMs, maxDelayMs, shouldRetry, signal },
    );
}

// ─── Backend reachability (the "Backend unreachable" banner) ────────────────
// data-service.js used to keep this as a private `_apiDisabled` that only its
// own proxy could flip back. The Site Visits surface talks to apiFetch
// directly, so its successes never reached the proxy and the banner latched
// red at 08:11 over a surface that was working. The state lives here so both
// the proxy (data-service.js) and apiFetch (data-service-api.js) can clear it
// without importing each other.

let _backendReachable = true;
const _reachabilityListeners = new Set<(reachable: boolean) => void>();

function notifyReachability() {
    for (const listener of _reachabilityListeners) {
        try { listener(_backendReachable); } catch (err: any) {
            console.warn('[Network] reachability listener threw:', err?.message);
        }
    }
}

export function isBackendReachable() {
    return _backendReachable;
}

/** Returns true when the state changed (and listeners were notified). */
export function setBackendReachable(next: boolean) {
    const value = !!next;
    if (_backendReachable === value) return false;
    _backendReachable = value;
    if (value) {
        resetBackendRecovery();
    } else {
        scheduleBackendRecoveryProbe();
    }
    notifyReachability();
    // Held background work runs after the listeners have heard the news, so a
    // listener that resumes the autosave is not overtaken by telemetry.
    if (value) drainDeferredBackendWork();
    return true;
}

/** Cheap and idempotent: no-op unless the banner is currently up. */
export function markBackendReachable() {
    return setBackendReachable(true);
}

export function markBackendUnreachable() {
    return setBackendReachable(false);
}

/** Re-send the current state to every listener (the Reconnect button uses it). */
export function emitBackendReachability() {
    notifyReachability();
}

export function subscribeBackendReachability(listener: (reachable: boolean) => void) {
    _reachabilityListeners.add(listener);
    return () => { _reachabilityListeners.delete(listener); };
}

// ─── QuoteMaker's server is not answering (outage lane, 2026-09-29) ─────────
// Field report: during a backend deploy the load balancer answered every
// request with an HTML 502 for ten minutes. The page treated the boot identity
// check's 502 as "signed out", the login form called it a wrong password,
// saves retried the whole project envelope, and harvest, embeddings, telemetry
// and Assistant saves each kept firing into the dead server. Nothing noticed
// when the server came back except the next request that happened to succeed.
//
// This block gives the client one answer to "is the server answering?":
//   • isServerUnavailableError  one classification (gateway 502/503/504, no
//                               response, auth backend down, timeouts),
//                               never 401/403, never a 500 the server wrote.
//   • a recovery loop           probes /api/health with backoff while the
//                               server is unreachable and flips the state
//                               back on the first healthy answer.
//   • ensureBackendAnswering    the gate paid and background work passes
//                               before it sends anything.
//   • runWhenBackendAnswers     holds background writes (latest per key)
//                               until the server answers, then runs each once.

export const SERVER_UNAVAILABLE_CODE = 'QM_SERVER_UNAVAILABLE';
// True for every gated caller: a save (the autosave sends it later), an AI call
// (not queued) and background work (held). So it promises nothing more.
export const SERVER_UNAVAILABLE_MESSAGE = "QuoteMaker is updating or can't reach its server, so this was not sent.";

const SERVER_UNAVAILABLE_CODES = new Set([
    SERVER_UNAVAILABLE_CODE,
    NETWORK_UNREACHABLE_CODE,
    'AUTH_BACKEND_UNAVAILABLE',
    'AUTH_REFRESH_TIMEOUT',
]);
const MAX_CAUSE_DEPTH = 6;

/**
 * True when the request failed because QuoteMaker's server did not answer:
 * a gateway 502/503/504, no HTTP response, the sign-in service being down,
 * or a timeout. False for anything the server itself decided (4xx, a 500
 * route bug) and for a caller's own abort. Walks `cause` so a wrapped
 * failure (media durability, save receipts) is still recognised.
 */
export function isServerUnavailableError(err: any): boolean {
    let cursor = err;
    const seen = new Set<unknown>();
    for (let depth = 0; cursor && typeof cursor === 'object' && depth < MAX_CAUSE_DEPTH; depth += 1) {
        if (seen.has(cursor)) break;
        seen.add(cursor);
        if (cursor.name === 'AbortError') return false;
        if (cursor.serverUnavailable === true) return true;
        if (typeof cursor.code === 'string' && SERVER_UNAVAILABLE_CODES.has(cursor.code)) return true;
        if (isTransientNetworkFailure(cursor)) return true;
        cursor = cursor.cause;
    }
    return false;
}

/**
 * The error a gated caller throws when it did not send anything because the
 * server is known not to answer. Status 0 ("no HTTP response"), so every
 * existing classifier (isUnreachableError, the project save retry policy)
 * reads it as a connectivity failure, and `notSent` so nobody reports the
 * outcome as unknown.
 */
export function createServerUnavailableError({
    label = 'Request',
    method = 'GET',
    status = 0,
    cause,
}: { label?: string; method?: string; status?: number; cause?: unknown } = {}) {
    const err = new Error(SERVER_UNAVAILABLE_MESSAGE) as Error & Record<string, unknown>;
    err.status = status;
    err.code = SERVER_UNAVAILABLE_CODE;
    err.label = label;
    err.method = String(method || 'GET').toUpperCase();
    err.qmSource = 'network-resilience';
    err.serverUnavailable = true;
    err.networkFailure = true;
    err.notSent = true;
    err.retryable = true;
    err.definitive = false;
    err.outcomeUnknown = false;
    if (cause !== undefined) err.cause = cause;
    return err;
}

/**
 * A gateway answered for the backend: 502/503/504 with a body that is not
 * the backend's JSON. The backend itself always replies JSON; an HTML body
 * with one of these statuses is the load balancer or proxy saying nothing is
 * behind it.
 */
export function isGatewayUnavailableResponse(status: unknown, bodyWasJson: boolean): boolean {
    return (status === 502 || status === 503 || status === 504) && !bodyWasJson;
}

const BACKEND_RECOVERY_DELAYS_MS = [2000, 4000, 8000, 15000, 30000];
const FRESH_PROBE_FAILURE_MS = 3000;
let _recoveryLoopEnabled = import.meta.env?.MODE !== 'test';
let _recoveryTimer: ReturnType<typeof setTimeout> | null = null;
let _recoveryAttempt = 0;
let _nextRecoveryProbeAt: number | null = null;
let _probeInFlight: Promise<boolean> | null = null;
let _lastProbeFailedAt = 0;

function resetBackendRecovery() {
    if (_recoveryTimer) clearTimeout(_recoveryTimer);
    _recoveryTimer = null;
    _recoveryAttempt = 0;
    _nextRecoveryProbeAt = null;
    _lastProbeFailedAt = 0;
}

function scheduleBackendRecoveryProbe() {
    if (!_recoveryLoopEnabled || _backendReachable || _recoveryTimer) return;
    const delay = BACKEND_RECOVERY_DELAYS_MS[Math.min(_recoveryAttempt, BACKEND_RECOVERY_DELAYS_MS.length - 1)]!;
    _recoveryAttempt += 1;
    _nextRecoveryProbeAt = Date.now() + delay;
    _recoveryTimer = setTimeout(() => {
        _recoveryTimer = null;
        void probeBackendNow();
    }, delay);
}

async function probeBackendHealth(fetchImpl: typeof fetch | undefined, timeoutMs = HEALTH_PROBE_TIMEOUT_MS) {
    if (typeof fetchImpl !== 'function') return false;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
        const timeout = new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => {
                controller?.abort();
                reject(new Error('QuoteMaker health check timed out'));
            }, timeoutMs);
        });
        const response: any = await Promise.race([
            fetchImpl('/api/health', {
                cache: 'no-store',
                credentials: 'same-origin',
                signal: controller?.signal,
            }),
            timeout,
        ]);
        if (!response?.ok) return false;
        // The backend always answers JSON. A 200 HTML page is something else
        // (a dev server's SPA fallback), and a health answer whose database
        // check failed cannot take a save either.
        if (typeof response.json !== 'function') return true;
        const body = await response.json().catch(() => null);
        if (!body || typeof body !== 'object') return false;
        if (Object.prototype.hasOwnProperty.call(body, 'database') && body.database !== 'ok') return false;
        return true;
    } catch {
        return false;
    } finally {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
    }
}

/**
 * Ask /api/health now. One probe at a time: concurrent callers share it. The
 * answer sets the reachability state, so a healthy answer takes the banner
 * down and releases held work, and a failed one keeps the recovery loop going.
 */
export function probeBackendNow(fetchImpl: typeof fetch | undefined = globalThis.fetch?.bind(globalThis)): Promise<boolean> {
    if (_probeInFlight) return _probeInFlight;
    const probe = (async () => {
        const ok = await probeBackendHealth(fetchImpl);
        if (ok) {
            setBackendReachable(true);
        } else {
            _lastProbeFailedAt = Date.now();
            if (!setBackendReachable(false)) scheduleBackendRecoveryProbe();
        }
        return ok;
    })();
    _probeInFlight = probe.finally(() => { _probeInFlight = null; });
    return _probeInFlight;
}

/**
 * The gate for anything that should not be sent into a dead server. Answers
 * true at once while the server is known to answer. While it is not, waits
 * for an in-flight probe or runs one; a probe that failed seconds ago is still
 * the answer, so a burst of callers costs one request, not one each.
 */
export function ensureBackendAnswering(): Promise<boolean> {
    if (_probeInFlight) return _probeInFlight;
    if (_backendReachable) return Promise.resolve(true);
    if (_lastProbeFailedAt && Date.now() - _lastProbeFailedAt < FRESH_PROBE_FAILURE_MS) {
        return Promise.resolve(false);
    }
    return probeBackendNow();
}

export function getBackendRecoveryState() {
    return {
        reachable: _backendReachable,
        probing: !!_probeInFlight,
        attempt: _recoveryAttempt,
        nextProbeAt: _backendReachable ? null : _nextRecoveryProbeAt,
    };
}

interface DeferredBackendWork {
    run: () => unknown;
    label: string;
    heldAt: number;
}
const MAX_DEFERRED_BACKEND_WORK = 50;
const _deferredBackendWork = new Map<string, DeferredBackendWork>();

/**
 * Run background work now if the server answers; otherwise hold it until it
 * does. A second job with the same key replaces the first (the latest
 * conversation snapshot wins). Held jobs run once each, in the order they
 * were last held, when the server answers again.
 */
export function runWhenBackendAnswers(key: string, run: () => unknown, { label }: { label?: string } = {}): 'ran' | 'deferred' {
    if (_backendReachable) {
        run();
        return 'ran';
    }
    _deferredBackendWork.delete(key);
    _deferredBackendWork.set(key, { run, label: label || key, heldAt: Date.now() });
    while (_deferredBackendWork.size > MAX_DEFERRED_BACKEND_WORK) {
        const oldest = _deferredBackendWork.keys().next().value;
        if (oldest === undefined) break;
        _deferredBackendWork.delete(oldest);
    }
    return 'deferred';
}

export function getDeferredBackendWorkCount() {
    return _deferredBackendWork.size;
}

function drainDeferredBackendWork() {
    if (_deferredBackendWork.size === 0) return;
    const jobs = [..._deferredBackendWork.values()];
    _deferredBackendWork.clear();
    for (const job of jobs) {
        try {
            const result = job.run() as any;
            if (result && typeof result.catch === 'function') {
                result.catch((err: any) => console.warn(`[Network] held "${job.label}" failed after reconnecting:`, err?.message || err));
            }
        } catch (err: any) {
            console.warn(`[Network] held "${job.label}" failed after reconnecting:`, err?.message || err);
        }
    }
}

/** Test seam: the recovery loop is off under Vitest unless a test turns it on. */
export function __setBackendRecoveryLoopEnabledForTests(enabled: boolean) {
    _recoveryLoopEnabled = !!enabled;
    if (!enabled) resetBackendRecovery();
}

/** Test seam: back to "the server answers", no loop, nothing held. */
export function __resetBackendAvailabilityForTests() {
    resetBackendRecovery();
    _probeInFlight = null;
    _deferredBackendWork.clear();
    _backendReachable = true;
    _recoveryLoopEnabled = false;
}

// ─── Timeout Wrapper ─────────────────────────────────────────────────────────

/**
 * Wrap a promise with a timeout. Rejects with TimeoutError if not settled in time.
 */
function withTimeout(promise: Promise<any>, ms: number) {
    if (!ms || ms <= 0) return promise;
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            reject(Object.assign(new Error(`Operation timed out after ${ms}ms`), { name: 'TimeoutError' }));
        }, ms);
        promise.then(
            (val) => { clearTimeout(timer); resolve(val); },
            (err) => { clearTimeout(timer); reject(err); }
        );
    });
}

// ─── Offline Queue ───────────────────────────────────────────────────────────
// When the user submits operations while offline, they're queued here.
// When connection is restored, they're drained in order.

interface QueueItem {
    fn: () => Promise<any>;
    label: string;
    resolve: (value: any) => void;
    reject: (err: any) => void;
    queuedAt: number;
}
const _queue: QueueItem[] = [];
let _draining = false;

/**
 * Queue an operation for execution when back online.
 * Returns a promise that resolves when the operation eventually executes.
 *
 * @param {Function} fn - Async function to execute
 * @param {string} [label] - Human-readable label for logging
 * @returns {Promise<any>}
 */
export function queueWhenOnline(fn: () => Promise<any>, label = 'queued operation') {
    if (_isOnline) {
        // Online - execute immediately with retry
        return withRetry(fn);
    }

    // Offline - queue for later
    return new Promise((resolve, reject) => {
        _queue.push({ fn, label, resolve, reject, queuedAt: Date.now() });
        console.log(`[Network] Queued "${label}" (${_queue.length} items in queue)`);
    });
}

/**
 * Drain the offline queue. Called automatically when connection is restored.
 */
async function drainQueue() {
    if (_draining || _queue.length === 0) return;
    _draining = true;

    console.log(`[Network] Draining ${_queue.length} queued operations...`);

    while (_queue.length > 0 && _isOnline) {
        const item = _queue.shift()!;
        try {
            const result = await withRetry(item.fn);
            item.resolve(result);
            console.log(`[Network] Queued "${item.label}" completed`);
        } catch (err: any) {
            item.reject(err);
            console.error(`[Network] Queued "${item.label}" failed:`, err.message);
        }
    }

    _draining = false;
}

/**
 * Get current queue status.
 */
export function getQueueStatus() {
    return {
        isOnline: _isOnline,
        queueLength: _queue.length,
        isDraining: _draining,
        items: _queue.map(q => ({ label: q.label, queuedAt: q.queuedAt })),
    };
}

// ─── Resilient Fetch ─────────────────────────────────────────────────────────

/**
 * A fetch wrapper with retry, timeout, and offline awareness.
 * Use this instead of raw fetch() for all backend calls.
 *
 * @param {string} url
 * @param {Object} [opts] - Standard fetch options + retry config
 * @param {number} [opts.maxAttempts=3]
 * @param {number} [opts.timeoutMs=30000]
 * @returns {Promise<Response>}
 */
export async function resilientFetch(url: string, opts: any = {}) {
    const { maxAttempts, timeoutMs, signal, ...fetchOpts } = opts;

    return withRetry(
        () => fetch(url, { ...fetchOpts, signal }),
        { maxAttempts: maxAttempts || 3, timeoutMs: timeoutMs || 30000, signal }
    );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function sleep(ms: number, signal?: AbortSignal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'));
        const onAbort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve(undefined);
        }, ms);
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}
